import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    untracked,
    ViewEncapsulation,
} from "@angular/core";
import { EventFormStore } from "../event.form.store";
import { DatetimeService } from "../../../services/datetime.service";
import { UserprefService } from "../../../services/userpref.service";
import { EventFormOccurrenceUtil } from "../../../modules/s25-event-creation-form/occurrences/event.form.occurrence.util";
import { S25InfiniteScrollModule } from "../../../modules/s25-infinite-scroll/s25.infinite.scroll.module";
import { S25Date } from "../../../util/core/s25.date";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { Bind } from "../../../decorators/bind.decorator";
import { EventFormDateOccurrenceTableRowComponent } from "./event.form.date.occurrence.table.row.component";
import { Event } from "../../../pojo/Event";
import Rsrv = Event.Reservation.States;
import type { DateOccurrenceRow } from "./event.form.date.occurrences.types";
import type { EventForm } from "..";
import type { S25ItemI } from "../../../pojo/S25ItemI";
import { Item } from "../../../pojo/Item";
import { confirmBoundEventLocationChangeForFormOccurrence } from "../../../modules/s25-event/utils/bound.event.location.util";

@Component({
    selector: "s25-ng-event-form-date-occurrences-table",
    host: {
        class: "ngDateOccTable",
        "[attr.id]": "tableId",
    },
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [EventFormDateOccurrenceTableRowComponent, S25InfiniteScrollModule],
    template: `
        @if (formats()) {
            <div
                class="ngDateOccScrollTopEl"
                s25-infinite-scroll
                [topSelector]="'self'"
                [onScroll]="loadMoreRows"
                [hasMorePages]="hasMoreClientPages"
                [minScrollAmount]="100"
                [ready]="scrollReadyPromise"
            >
                @if (eventForm.model().hasSearch) {
                    <div>
                        <p class="ngFinePrint c-margin-bottom--half">
                            You may select which occurrences to be included in your availability checks and searches.
                            Adding an item in search will only add it for the occurrences selected below.
                        </p>

                        @if (eventForm.model().hasLocationSearch) {
                            <button
                                type="button"
                                class="aw-button aw-button--outline"
                                (click)="onlyMissing('locations')"
                            >
                                Include Only Missing Locations
                            </button>
                        }

                        @if (eventForm.model().hasResourceSearch) {
                            <button
                                type="button"
                                class="aw-button aw-button--outline"
                                (click)="onlyMissing('resources')"
                            >
                                Include Only Missing Resources
                            </button>
                        }

                        <button type="button" (click)="toggleViewIncluded()" class="aw-button aw-button--outline">
                            View {{ viewIncluded() ? "All" : "Included" }}
                        </button>

                        <button type="button" (click)="addOccurrence()" class="aw-button aw-button--outline">
                            + Add Occurrence
                        </button>
                    </div>
                }
                <table
                    class="rose-object-occurrences-table c-margin-top--half"
                    [class.single-occurrence]="visibleRows().length === 1"
                >
                    <thead>
                        <tr>
                            @if (eventForm.model().hasSearch) {
                                <th>Include In Search</th>
                            }
                            <th>Dates</th>
                            <th>Times</th>
                            @if (extraTimesExist()) {
                                <th>Extra Times</th>
                            }
                            <th>Comment</th>
                            <th>State</th>
                            @if (locationsExist()) {
                                <th>Locations</th>
                            }
                            @if (resourcesExist()) {
                                <th>Resources</th>
                            }
                            <th>Remove</th>
                        </tr>
                    </thead>
                    <tbody class="ngDateOccContainer rose-date-occ-container">
                        @for (row of visibleRows(); track row.occ.uuid; let rowIndex = $index) {
                            <tr
                                s25-ng-event-form-date-occurrence-row
                                [row]="row"
                                [dateFormat]="formats()!.dateFormat"
                                [dateTimeFormat]="formats()!.dateTimeFormat"
                                [showDateEditor]="newOcc() && rowIndex === visibleRows().length - 1"
                                [justAdded]="justAddedUuid() === row.occ.uuid"
                                [extraTimesExist]="extraTimesExist()"
                                [locationsExist]="locationsExist()"
                                [resourcesExist]="resourcesExist()"
                                [shrinkTimePicker]="extraTimesExist() && (locationsExist() || resourcesExist())"
                                [showExtraTimes]="showExtraTimes()"
                                (includeInSearchChange)="onIncludeInSearchChange()"
                                (stateChange)="onStateChange(row, $event)"
                                (removeLocation)="removeLocation(row, $event)"
                                (removeResource)="removeResource(row, $event)"
                                (dateFieldsChange)="onDateFieldsChange(row)"
                                (timeFieldsChange)="onTimeFieldsChange(row)"
                            ></tr>
                        }
                    </tbody>
                </table>
            </div>
        }
    `,
})
export class EventFormDateOccurrencesTableComponent {
    readonly date = input<Date | null>(null);
    readonly dateFormat = input<string>();
    readonly dateTimeFormat = input<string>();

    readonly closeRequested = output<void>();

    readonly eventForm = inject(EventFormStore);

    // TODO: replace all usages of Event.Form with EventForm
    private asEventForm(formModel: EventForm): Event.Form {
        return formModel as unknown as Event.Form;
    }

    readonly tableId = `ngDateOcc-${Date.now()}`;
    private readonly pageSize = 10;

    readonly dateFormats = s25Resource({
        if: () => !this.dateFormat() || !this.dateTimeFormat(),
        loader: async () => {
            const [dateTimeFormat, dateFormat] = await Promise.all([
                UserprefService.getS25DateTimeformat(),
                UserprefService.getS25Dateformat(),
            ]);
            return { dateTimeFormat, dateFormat };
        },
    });

    readonly formats = computed(() => {
        const dateFormat = this.dateFormat();
        const dateTimeFormat = this.dateTimeFormat();
        if (dateFormat && dateTimeFormat) {
            return { dateFormat, dateTimeFormat };
        }
        return this.dateFormats.value();
    });

    private readonly formatKey = computed(() => {
        const formats = this.formats();
        return formats ? `${formats.dateFormat}\0${formats.dateTimeFormat}` : "";
    });

    readonly scrollReadyPromise = Promise.resolve();

    readonly occOnDate = signal<DateOccurrenceRow[]>([]);
    readonly pagesShown = signal(1);
    readonly viewIncluded = signal(false);
    readonly newOcc = signal(false);
    readonly justAddedUuid = signal<number | null>(null);
    readonly showExtraTimes = signal(true);
    readonly locationsExist = signal(false);
    readonly resourcesExist = signal(false);
    readonly extraTimesExist = signal(false);
    readonly scrollWorking = signal(false);

    readonly itemsLimit = computed(() => this.pageSize * this.pagesShown());
    readonly visibleRows = computed(() => this.occOnDate().slice(0, this.itemsLimit()));

    constructor() {
        effect(() => {
            const formatKey = this.formatKey();
            this.date();
            if (!formatKey) {
                return;
            }
            untracked(() => {
                if (!this.occOnDate().length) {
                    this.refresh();
                }
            });
        });
    }

    hasMoreClientPages = (): boolean => this.itemsLimit() < this.occOnDate().length;

    @Bind
    async loadMoreRows(): Promise<void> {
        if (this.scrollWorking()) {
            return;
        }
        this.scrollWorking.set(true);
        if (this.hasMoreClientPages()) {
            this.pagesShown.update((pages) => pages + 1);
        }
        await S25Util.delay(250);
        this.scrollWorking.set(false);
    }

    addOccurrence(): void {
        void this.createOccurrence();
    }

    @Bind
    toggleViewIncluded(): void {
        this.viewIncluded.update((included) => !included);
        if (this.viewIncluded()) {
            const formModel = this.eventForm.model();
            const filtered = (formModel.occurrences ?? [])
                .filter((occ: EventForm.Occurrence) => occ.includeInSearch)
                .map((occ) => this.decorateOccurrenceRow(formModel, occ));
            this.refresh(filtered);
        } else {
            this.refresh();
        }
    }

    onlyMissing(type: "locations" | "resources"): void {
        const formModel = this.eventForm.model();
        const missingOccs = EventFormOccurrenceUtil.getOccMissingObj(
            this.asEventForm(formModel),
            type,
        ) as EventForm.Occurrence[];

        for (const occ of formModel.occurrences ?? []) {
            occ.includeInSearch = false;
        }
        for (const occ of missingOccs) {
            occ.includeInSearch = true;
        }

        this.refresh();
        formModel.setSearchesDirty?.();
        this.eventForm.bump();
    }

    onIncludeInSearchChange(): void {
        this.eventForm.model().setSearchesDirty?.();
        this.eventForm.bump();
    }

    onStateChange(row: DateOccurrenceRow, state: S25ItemI): void {
        row.occ.state = state;
        row.states = this.buildStates(row.occ);
        this.occOnDate.update((rows) => [...rows]);
        this.eventForm.model().refreshOccAvailability?.(row.occ);
        this.eventForm.bump();
    }

    onDateFieldsChange(row: DateOccurrenceRow): void {
        this.eventForm.model().refreshOccAvailability?.(row.occ);
        this.eventForm.bump();
    }

    onTimeFieldsChange(row: DateOccurrenceRow): void {
        const occ = row.occ;
        const nextStartTime = S25Util.date.toS25ISOTimeStr(occ.evStartDt);
        const nextEndTime = S25Util.date.toS25ISOTimeStr(occ.evEndDt);
        if (occ.prevStartTime === nextStartTime && occ.prevEndTime === nextEndTime) {
            return;
        }
        occ.prevStartTime = nextStartTime;
        occ.prevEndTime = nextEndTime;
        this.eventForm.model().refreshOccAvailability?.(occ);
        this.showExtraTimes.set(false);
        Object.assign(row, this.decorateOccurrenceRow(this.eventForm.model(), occ));
        this.eventForm.bump();
        void S25Util.delay(0).then(() => this.showExtraTimes.set(true));
    }

    async removeLocation(row: DateOccurrenceRow, location: S25ItemI): Promise<void> {
        const formModel = this.eventForm.model();
        if (
            location.itemTypeId === Item.Ids.Location &&
            !(await confirmBoundEventLocationChangeForFormOccurrence("remove", formModel))
        ) {
            return;
        }
        this.removeObject(row.occ.rsrvId, location);
        row.locations = row.locations.filter((item) => item.itemId !== location.itemId);
    }

    removeResource(row: DateOccurrenceRow, resource: S25ItemI): void {
        this.removeObject(row.occ.rsrvId, resource);
        row.resources = row.resources.filter((item) => item.itemId !== resource.itemId);
    }

    removeOccurrenceByUuid(occUuid: string): void {
        const row = this.occOnDate().find((candidate) => String(candidate.occ.uuid) === occUuid);
        if (row) {
            this.removeOccurrence(row);
        }
    }

    removeOccurrence(row: DateOccurrenceRow): void {
        this.newOcc.set(false);
        const formModel = this.eventForm.model();
        const occurrence = formModel.occurrences?.find((candidate) => candidate.uuid === row.occ.uuid) ?? row.occ;
        formModel.removeOccurrence?.(occurrence);
        this.eventForm.bump();
        const occDateIdx = this.occOnDate().indexOf(row);
        const nextRows = this.occOnDate().slice();
        if (occDateIdx > -1) {
            nextRows.splice(occDateIdx, 1);
        }
        this.occOnDate.set(nextRows);
        this.updateColumnFlags(nextRows);
        this.updateModalBodyOverflow(nextRows.length);

        if (!nextRows.length) {
            formModel.clickToPickApiBean?.totalCalendarRefresh?.(false);
        }

        const refreshPromise = formModel.refreshOccAvailability?.(occurrence);
        const onRefreshSettled = () => {
            if (!nextRows.length) {
                this.closeRequested.emit();
            }
        };
        if (window.angBridge?.$q) {
            void window.angBridge.$q.when(refreshPromise).then(onRefreshSettled);
        } else {
            void Promise.resolve(refreshPromise).then(onRefreshSettled);
        }
    }

    private buildStates(occ: EventForm.Occurrence): S25ItemI[] {
        const activeState: S25ItemI = { itemId: Rsrv.Active, itemName: "Active" };
        const cancelledState: S25ItemI = { itemId: Rsrv.Cancelled, itemName: "Cancelled" };
        const states: S25ItemI[] = [cancelledState];
        const currentStateId = S25Util.parseInt(occ.state.itemId);

        if ([Rsrv.Exception, Rsrv.Override].includes(currentStateId)) {
            activeState.itemId = Rsrv.Override;
            activeState.itemName = "Override";

            if (currentStateId === Rsrv.Override) {
                states.push(
                    activeState,
                    { itemId: Rsrv.Exception, itemName: "Exception" },
                    { itemId: Rsrv.Warning, itemName: "Warning" },
                );
            } else {
                states.push(activeState, occ.state);
            }
        } else if (currentStateId === Rsrv.Warning || currentStateId === Rsrv.Active) {
            states.push(occ.state);
        } else {
            states.push(activeState);
        }

        return states;
    }

    private async createOccurrence(): Promise<void> {
        this.newOcc.set(true);
        const formModel = this.eventForm.model();
        const [currentHour, nextHour] = await Promise.all([DatetimeService.currentHour(), DatetimeService.nextHour()]);

        const filterDate = this.date();
        const evStart = S25Util.date.clone(filterDate || currentHour);
        let evEndDt = S25Util.date.clone(filterDate || currentHour);

        const currHour = currentHour.getHours();
        let nextHourValue = nextHour.getHours();
        let nextMin = 0;

        if (nextHourValue < currHour) {
            nextHourValue = currHour;
            nextMin = 59;
        }

        evStart.setHours(currHour);
        evStart.setMinutes(0);
        evEndDt.setHours(nextHourValue);
        evEndDt.setMinutes(nextMin);

        if (formModel.timeModel.evStartDt && formModel.timeModel.evEndDt) {
            evEndDt = S25Date.addDays(
                evEndDt,
                S25Date.diffDays(formModel.timeModel.evStartDt, formModel.timeModel.evEndDt),
            );
        }

        const newOcc = formModel.addOccurrence?.(
            evStart,
            evEndDt,
            formModel.timeModel.minutes,
            false,
            formModel.occurrences?.length ? "adhoc" : "dnr",
            formModel.locations ?? [],
            formModel.resources ?? [],
        );

        if (!newOcc) {
            return;
        }
        this.eventForm.bump();

        const decorated = this.decorateOccurrenceRow(formModel, newOcc);
        const nextRows = [...this.occOnDate(), decorated];
        this.occOnDate.set(nextRows);
        this.updateColumnFlags(nextRows);

        const itemNum = nextRows.length;
        this.pagesShown.set(Math.max(this.pagesShown(), Math.ceil(itemNum / this.pageSize) || 1));
        this.updateModalBodyOverflow(itemNum);
        formModel.refreshOccAvailability?.(newOcc);

        this.justAddedUuid.set(newOcc.uuid);
        await S25Util.delay(250);
        this.focusNewlyAddedOccurrence();
    }

    private refresh(nextOccList?: DateOccurrenceRow[]): void {
        const rows = nextOccList ?? this.getOccsOnDate();
        this.pagesShown.set(Math.max(1, Math.ceil(rows.length / this.pageSize) || 1));
        this.occOnDate.set(rows);
        this.updateColumnFlags(rows);
        this.updateModalBodyOverflow(rows.length);
    }

    private getOccsOnDate(): DateOccurrenceRow[] {
        const formModel = this.eventForm.model();
        return EventFormOccurrenceUtil.getOccOnDate(formModel.occurrences, this.date(), true).map(
            (occ: EventForm.Occurrence) => this.decorateOccurrenceRow(formModel, occ),
        );
    }

    private decorateOccurrenceRow(formModel: EventForm, occ: EventForm.Occurrence): DateOccurrenceRow {
        const minutes = formModel.timeModel.minutes;
        occ.prevStartTime = S25Util.date.toS25ISOTimeStr(occ.evStartDt);
        occ.prevEndTime = S25Util.date.toS25ISOTimeStr(occ.evEndDt);
        return {
            occ,
            states: this.buildStates(occ),
            locations: EventFormOccurrenceUtil.getOccLocations(
                this.asEventForm(formModel),
                occ as unknown as Event.Occurrence,
                true,
            ),
            resources: EventFormOccurrenceUtil.getOccResources(
                this.asEventForm(formModel),
                occ as unknown as Event.Occurrence,
                true,
            ),
            setupDate:
                minutes.setupMinutes === 0
                    ? null
                    : S25Util.date.addMinutes(
                          occ.evStartDt,
                          -1 * ((minutes.setupMinutes || 0) + (minutes.preMinutes || 0)),
                      ),
            preDate:
                minutes.preMinutes === 0
                    ? null
                    : S25Util.date.addMinutes(occ.evStartDt, -1 * (minutes.preMinutes || 0)),
            postDate: minutes.postMinutes === 0 ? null : S25Util.date.addMinutes(occ.evEndDt, minutes.postMinutes || 0),
            takedownDate:
                minutes.takedownMinutes === 0
                    ? null
                    : S25Util.date.addMinutes(occ.evEndDt, (minutes.takedownMinutes || 0) + (minutes.postMinutes || 0)),
        };
    }

    private updateColumnFlags(rows: DateOccurrenceRow[]): void {
        let locationsExist = false;
        let resourcesExist = false;
        let extraTimesExist = false;

        for (const row of rows) {
            if (row.locations.length > 0) {
                locationsExist = true;
            }
            if (row.resources.length > 0) {
                resourcesExist = true;
            }
            if (row.setupDate || row.preDate || row.postDate || row.takedownDate) {
                extraTimesExist = true;
            }
        }

        this.locationsExist.set(locationsExist);
        this.resourcesExist.set(resourcesExist);
        this.extraTimesExist.set(extraTimesExist);
    }

    private removeObject(occId: number | undefined, obj: S25ItemI): void {
        const formModel = this.eventForm.model();
        const objOccs = formModel.getObjOccs?.() ?? [];
        const objOcc = objOccs.find(
            (item) =>
                item.occ.rsrvId === occId && item.item.itemId === obj.itemId && item.item.itemTypeId === obj.itemTypeId,
        );
        if (!objOcc) {
            return;
        }
        objOcc.isIncluded = false;
        formModel.refreshObjOccAvailability?.(objOcc.item, [objOcc.occ]);
        this.eventForm.bump();
    }

    private updateModalBodyOverflow(itemCount: number): void {
        const modalBody = document.querySelector(".event-form-date-occurrences .modal-body");
        if (!modalBody) {
            return;
        }
        if (itemCount > 4) {
            modalBody.classList.remove("ngDateOccDropdown");
        } else {
            modalBody.classList.add("ngDateOccDropdown");
        }
    }

    private focusNewlyAddedOccurrence(): void {
        const newRow = document.querySelector(".ngEventFormOcc.ng-event-form-occ--just-added");
        if (!newRow) {
            return;
        }

        newRow.scrollIntoView({ behavior: "smooth", block: "nearest" });

        const focusTarget =
            (newRow.querySelector("td[data-label='Dates'] input:not([type='hidden'])") as HTMLElement | null) ||
            (newRow.querySelector("td[data-label='Times'] input:not([type='hidden'])") as HTMLElement | null) ||
            (newRow.querySelector("input.search-checkbox") as HTMLElement | null) ||
            (newRow.querySelector("input[aria-label='Comment']") as HTMLElement | null);

        let previousAriaLabel: string | null = null;
        let hadAriaLabel = false;
        if (focusTarget) {
            hadAriaLabel = focusTarget.hasAttribute("aria-label");
            previousAriaLabel = focusTarget.getAttribute("aria-label");
            focusTarget.setAttribute(
                "aria-label",
                previousAriaLabel ? `Occurrence added. ${previousAriaLabel}` : "Occurrence added",
            );
            void S25Util.delay(0).then(() => focusTarget.focus({ preventScroll: true }));
        }

        void S25Util.delay(2000).then(() => {
            if (focusTarget) {
                if (hadAriaLabel && previousAriaLabel) {
                    focusTarget.setAttribute("aria-label", previousAriaLabel);
                } else {
                    focusTarget.removeAttribute("aria-label");
                }
            }
            this.justAddedUuid.set(null);
        });
    }
}
